import React, {useEffect, useMemo, useRef, useState} from "react";
import {Link, useNavigate, useParams} from 'react-router-dom';
import axios from "axios";
import {toast} from "react-toastify";
// @ts-ignore
import {
	PencilSimple,
	PaperPlaneTilt,
	FloppyDisk,
	CaretLeft,
	CaretRight,
	X,
	Tag,
	Sparkle,
	Eye,
	Desktop,
	DeviceTablet,
	DeviceMobile,
	Copy,
	Trash,
} from "@phosphor-icons/react";
import {getAxiosBaseUrl, getBaseUrlNodeServer} from "../../config";
import {SigninForm} from "../molecules/SigninForm";
import CategorySelectionButtons from "../Campaign/CategorySelectionButtons";
import {initGrapesjs} from "../../lib/grapesjsConfig";
import {renderMjml} from "../../lib/mjmlPreview";
import {inferCategory} from "../../lib/templateCategories";
import {
	generateMjmlTemplate,
	getStoredKey,
	getLastUsedModel,
	setLastUsedModel,
	AVAILABLE_MODELS,
	AnthropicError,
} from "../../lib/anthropicClient";

const EXAMPLE_PROMPTS = [
	{ label: 'Welcome series email', prompt: 'A warm welcome email for new subscribers to a modern B2B SaaS product. Friendly tone, single hero CTA "Explore the product", quick "what to expect" bullets, and a soft sign-off from the founder.' },
	{ label: 'Product launch announcement', prompt: 'A product launch announcement for a new feature called "[FEATURE NAME]". Include: bold hero with product name, 3 feature highlights with icons, social proof quote, and a primary CTA "Try it now".' },
	{ label: 'Newsletter round-up', prompt: 'A monthly newsletter round-up. Sections: editor\'s note, 3 article cards with image + headline + 1-line summary + read-more link, an "in case you missed it" box, and a footer with social links.' },
	{ label: 'Re-engagement nudge', prompt: 'A short re-engagement email to subscribers who haven\'t opened our emails in 60 days. Honest tone, ask if they still want to hear from us, two CTAs: "Stay subscribed" and "Update preferences".' },
];

const PLACEHOLDER_SUBJECT = 'Welcome to Neugence Marketing';
const EMPTY_MJML = '<mjml><mj-body><mj-section><mj-column><mj-text>Start typing...</mj-text></mj-column></mj-section></mj-body></mjml>';

// localStorage keys.
const LS_VIEW_KEY = 'editor.lastView';            // 'preview' | 'edit'
const LS_TEST_EMAILS_KEY = 'editor.testEmails';   // string[] (most-recent first, max 5)
const TEST_EMAILS_MAX = 5;
const SEND_FROM_DISPLAY = 'no-reply@whipscribe.com';

// Preview iframe widths (px). Desktop is intentionally narrow-ish to match a
// typical "in-Gmail" reading column rather than full-bleed monitor — most email
// clients clamp content to ~600px anyway.
const DEVICE_WIDTHS = { desktop: 680, tablet: 768, mobile: 375 };

function readStoredView() {
	try {
		const v = localStorage.getItem(LS_VIEW_KEY);
		return v === 'edit' || v === 'preview' ? v : null;
	} catch (e) {
		return null;
	}
}

function writeStoredView(v) {
	try { localStorage.setItem(LS_VIEW_KEY, v); } catch (e) { /* noop */ }
}

function readStoredTestEmails() {
	try {
		const raw = localStorage.getItem(LS_TEST_EMAILS_KEY);
		if (!raw) return [];
		const arr = JSON.parse(raw);
		return Array.isArray(arr) ? arr.filter((s) => typeof s === 'string' && s).slice(0, TEST_EMAILS_MAX) : [];
	} catch (e) {
		return [];
	}
}

function writeStoredTestEmails(list) {
	try { localStorage.setItem(LS_TEST_EMAILS_KEY, JSON.stringify(list.slice(0, TEST_EMAILS_MAX))); } catch (e) { /* noop */ }
}

function pushTestEmail(email) {
	const e = (email || '').trim();
	if (!e) return readStoredTestEmails();
	const existing = readStoredTestEmails().filter((x) => x.toLowerCase() !== e.toLowerCase());
	const next = [e, ...existing].slice(0, TEST_EMAILS_MAX);
	writeStoredTestEmails(next);
	return next;
}

function formatRelative(ts, tickKey) {
	if (!ts) return null;
	const diffSec = Math.max(0, Math.floor((Date.now() - ts) / 1000));
	if (diffSec < 5) return 'Saved just now';
	if (diffSec < 60) return `Saved ${diffSec}s ago`;
	const m = Math.floor(diffSec / 60);
	if (m < 60) return `Saved ${m}m ago`;
	const h = Math.floor(m / 60);
	if (h < 24) return `Saved ${h}h ago`;
	return 'Saved a while ago';
}

// Emerald theme overrides for grapesjs default chrome.
const grapesjsThemeCss = `
.gjs-one-bg { background-color: #f8fafc; }
.gjs-two-color { color: #334155; }
.gjs-three-bg { background-color: #10b981; color: #fff; }
.gjs-four-color, .gjs-four-color-h:hover { color: #10b981; }

.gjs-pn-panel { background-color: #ffffff; border-color: #e2e8f0; }
.gjs-pn-btn { color: #475569; border-radius: 8px; transition: background-color .15s ease, color .15s ease; }
.gjs-pn-btn:hover { color: #0f172a; background-color: #f1f5f9; }
.gjs-pn-btn.gjs-pn-active { color: #047857; background-color: #d1fae5; box-shadow: none; }

.gjs-cv-canvas { background-color: #f8fafc; }

.gjs-block { border-radius: 10px; border: 1px solid #e2e8f0; background-color: #ffffff; transition: border-color .15s ease, box-shadow .15s ease; }
.gjs-block:hover { border-color: #10b981; box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06); }
.gjs-block-label { color: #475569; font-size: 11px; }
.gjs-block-category .gjs-title { color: #0f172a; font-weight: 600; background-color: #f8fafc; }

.gjs-toolbar { background-color: #10b981; }
.gjs-toolbar-item { color: #ffffff; }
.gjs-toolbar-item:hover { background-color: #059669; }

.gjs-sm-sector .gjs-sm-title { background-color: #f8fafc; color: #0f172a; border-bottom: 1px solid #e2e8f0; }
.gjs-sm-sector.gjs-sm-open .gjs-sm-title { color: #047857; }
.gjs-field { background-color: #ffffff; border: 1px solid #e2e8f0; border-radius: 6px; color: #0f172a; }
.gjs-field input, .gjs-field select, .gjs-field textarea { color: #0f172a; }
.gjs-clm-tags .gjs-sm-label, .gjs-sm-label { color: #475569; }

.gjs-rte-toolbar { background-color: #ffffff; border: 1px solid #e2e8f0; }
.gjs-rte-action { color: #475569; }
.gjs-rte-action:hover, .gjs-rte-active { color: #047857; background-color: #d1fae5; }

.gjs-mdl-dialog { background-color: #ffffff; color: #0f172a; border-radius: 12px; }
.gjs-mdl-header { background-color: #f8fafc; border-bottom: 1px solid #e2e8f0; }
.gjs-mdl-title { color: #0f172a; }
.gjs-btn-prim { background-color: #10b981; color: #ffffff; border-radius: 9999px; }
.gjs-btn-prim:hover { background-color: #059669; }

.gjs-selected { outline: 2px solid #10b981 !important; }
.gjs-hovered { outline: 1px dashed #10b981 !important; }
`;

export default function EditorAlone(props) {
	var signedin = sessionStorage.getItem("signedin");
	const isAdmin = sessionStorage.getItem('isAdmin');

	const {templateId} = useParams();
	const navigate = useNavigate();

	const [parentDocId, setparentDocId] = useState(templateId);

	// Re-sync state when react-router gives us a new :templateId (e.g. when
	// the user clicks a thumbnail in the preview-mode browse strip). Without
	// this, useState(templateId) only captures the initial value and the
	// editor stays stuck on the old template even though the URL changed.
	useEffect(() => {
		if (templateId !== parentDocId) {
			setparentDocId(templateId || null);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [templateId]);
	const [mjmlString, setMjmlString] = useState('');
	const [savedMjml, setSavedMjml] = useState('');
	const [selectedCategories, setSelectedCategories] = useState([]);
	const [savedCategories, setSavedCategories] = useState([]);

	const [subject, setSubject] = useState(PLACEHOLDER_SUBJECT);
	const [savedSubject, setSavedSubject] = useState(PLACEHOLDER_SUBJECT);

	const [editingSubject, setEditingSubject] = useState(false);
	const [subjectDraft, setSubjectDraft] = useState('');
	const subjectInputRef = useRef(null);

	// Inline send-test row state.
	const [testEmail, setTestEmail] = useState('');
	const [testEmailHistory, setTestEmailHistory] = useState(() => readStoredTestEmails());
	const [testEmailError, setTestEmailError] = useState('');
	const testEmailInputRef = useRef(null);

	const [showCategoryBar, setShowCategoryBar] = useState(false);

	// Verified sender identities (loaded once after mount; needed by inline Send test).
	const [verifiedSenders, setVerifiedSenders] = useState([]); // string[]
	const [sendersLoaded, setSendersLoaded] = useState(false);
	const [sendersLoading, setSendersLoading] = useState(false);
	const [sendFromAddr, setSendFromAddr] = useState('');

	const [saving, setSaving] = useState(false);
	const [sendingTest, setSendingTest] = useState(false);
	const [lastSavedAt, setLastSavedAt] = useState(null);
	const [tickKey, setTickKey] = useState(0);

	// Ask Claude (BYO Anthropic key) modal state.
	const [showAskClaude, setShowAskClaude] = useState(false);
	const [askPrompt, setAskPrompt] = useState('');
	const [askModel, setAskModel] = useState(getLastUsedModel());
	const [generating, setGenerating] = useState(false);

	// Loading: true while we're fetching the template AND/OR before grapesjs is mounted.
	const [templateLoading, setTemplateLoading] = useState(!!templateId);
	const [editorReady, setEditorReady] = useState(false);

	// View mode: 'preview' or 'edit'. Default policy:
	//   - Existing template (templateId in route) → preview, unless the user
	//     previously toggled to edit in this browser (LS_VIEW_KEY === 'edit').
	//   - Brand-new blank (/editor with no id) → edit (so they can start designing).
	// The user-flipped value persists across opens via localStorage.
	const [viewMode, setViewMode] = useState(() => {
		const stored = readStoredView();
		if (templateId) {
			return stored === 'edit' ? 'edit' : 'preview';
		}
		return 'edit';
	});
	const [previewDevice, setPreviewDevice] = useState('desktop'); // 'desktop' | 'tablet' | 'mobile'

	// All of the user's saved templates — populates the preview-mode browse
	// strip so they can flip between templates without bouncing back to
	// /savedtemplates. Fetched once on mount; cheap (~10 docs).
	const [myTemplates, setMyTemplates] = useState([]); // [{_id, subject, content, html, categories}]
	const [stripCategory, setStripCategory] = useState('all');
	// Mirror myTemplates in a ref so the per-parentDocId load effect can read
	// the latest cache without re-running every time the cache populates.
	const myTemplatesRef = useRef([]);
	useEffect(() => { myTemplatesRef.current = myTemplates; }, [myTemplates]);

	// grapesjs editor instance + container ref
	const editorContainerRef = useRef(null);
	const editorRef = useRef(null);
	// Latest onChange handler — kept in a ref so the grapesjs instance always
	// calls the freshest closure (which sees current state setters).
	const onChangeRef = useRef(() => {});
	// First-emit-after-load latch: grapesjs round-trips the loaded MJML
	// (parse → re-serialize) and the result usually differs in whitespace /
	// attribute order from what we got off the API. Without this latch, every
	// freshly-opened template starts with `mjmlDirty === true` and the Save
	// button looks enabled even though the user hasn't typed anything. The
	// fix: on the FIRST onChange after the editor mounts (or after a new
	// template id is loaded), treat that emission as the new saved baseline
	// instead of as a user edit.
	const firstEmitAfterLoadRef = useRef(true);

	// Stable onChange wrapper that updates state whenever doc changes.
	const handleMjmlChange = (newMjml) => {
		const next = newMjml || '';
		setMjmlString(next);
		if (firstEmitAfterLoadRef.current) {
			firstEmitAfterLoadRef.current = false;
			// Re-baseline so the Save button stays disabled until the user
			// actually edits something.
			setSavedMjml(next);
		}
	};
	onChangeRef.current = handleMjmlChange;

	// Load template (or set up empty doc) whenever parentDocId changes.
	useEffect(() => {
		window.scrollTo(0, 0);
		let cancelled = false;
		// Reset the first-emit latch so the next onChange after this load
		// becomes the new saved baseline.
		firstEmitAfterLoadRef.current = true;

		async function getUserSelectedTemplate() {
			// New-template path: nothing to fetch.
			if (parentDocId == null || parentDocId === undefined) {
				if (!cancelled) {
					setMjmlString('');
					setSavedMjml('');
					setSubject(PLACEHOLDER_SUBJECT);
					setSavedSubject(PLACEHOLDER_SUBJECT);
					setTemplateLoading(false);
				}
				return;
			}

			// Cache hot-swap: if we've already loaded this template in the
			// strip, hydrate state from the cache synchronously and skip the
			// network fetch + spinner. This makes preview-mode prev/next/
			// thumbnail navigation feel instant instead of refresh-flashing.
			const cached = myTemplatesRef.current.find(t => t._id === parentDocId);
			if (cached && cached.content) {
				setSubject(cached.subject || PLACEHOLDER_SUBJECT);
				setSavedSubject(cached.subject || PLACEHOLDER_SUBJECT);
				setSelectedCategories(cached.categories || []);
				setSavedCategories(cached.categories || []);
				setMjmlString(cached.content);
				setSavedMjml(cached.content);
				setTemplateLoading(false);
				// If the live grapesjs instance is around, hot-swap its
				// document so the user can flip back to edit mode without a
				// remount. setComponents triggers an onChange, but the
				// firstEmitAfterLoadRef latch (set above) re-baselines the
				// dirty tracker, so Save stays correctly disabled.
				const editor = editorRef.current;
				if (editor && typeof editor.setComponents === 'function') {
					try { editor.setComponents(cached.content); } catch (e) { /* noop */ }
				}
				return;
			}

			setTemplateLoading(true);
			try {
				const response = await fetch(getBaseUrlNodeServer() + 'getusertemplate/' + parentDocId, {
					method: 'post',
					headers: {'Content-Type': 'application/json'},
				});
				const arr = await response.json();
				const row = arr && arr[0];
				if (cancelled || !row) return;

				const loadedMjml = row.content || '';
				const loadedSubject = row.subject || PLACEHOLDER_SUBJECT;
				if (Array.isArray(row.selectedCategories)) {
					setSelectedCategories(row.selectedCategories);
					setSavedCategories(row.selectedCategories);
				}
				setSubject(loadedSubject);
				setSavedSubject(loadedSubject);
				setMjmlString(loadedMjml);
				setSavedMjml(loadedMjml);
				// Hot-swap the live grapesjs document too so its internal tree
				// matches the freshly-loaded template. Without this, any later
				// onChange emit from grapesjs re-serializes the PRIOR template's
				// tree and clobbers the mjmlString we just set.
				const editor2 = editorRef.current;
				if (editor2 && typeof editor2.setComponents === 'function') {
					try { editor2.setComponents(loadedMjml); } catch (e) { /* noop */ }
				}
			} catch (error) {
				console.error('Error during fetch:', error.message);
				toast.error('Could not load template');
			} finally {
				if (!cancelled) setTemplateLoading(false);
			}
		}

		getUserSelectedTemplate();
		return () => {
			cancelled = true;
		};
		// We intentionally only re-run when parentDocId changes.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [parentDocId]);

	// Mount grapesjs once the template is loaded (or we know it's a new doc).
	useEffect(() => {
		if (templateLoading) return;
		if (!editorContainerRef.current) return;
		if (editorRef.current) return; // already mounted

		// For an existing template we use the freshly-loaded mjml; for a brand
		// new template (no id) we hand grapesjs a minimal valid stub so the
		// canvas isn't completely empty on first render.
		const initial = mjmlString || (parentDocId == null ? EMPTY_MJML : '');
		try {
			const editor = initGrapesjs(editorContainerRef.current, {
				initialMjml: initial,
				onChange: (mjml) => onChangeRef.current(mjml),
			});
			editorRef.current = editor;

			// Strip the GrapesJS native fullscreen button. The user finds it
			// useless; the editor already fills the viewport here. The mjml
			// plugin keeps undo/redo/import/export on the same panel, so we
			// remove just the one button by id.
			//
			// `Panels.removeButton` is safe even if the button is missing in a
			// future plugin version — it returns null silently.
			try {
				const panels = editor.Panels;
				if (panels && typeof panels.removeButton === 'function') {
					panels.removeButton('options', 'fullscreen');
				}
			} catch (e) {
				// eslint-disable-next-line no-console
				console.warn('[editor] could not remove fullscreen button:', e && e.message);
			}

			setEditorReady(true);
		} catch (e) {
			console.error('grapesjs init failed:', e);
			toast.error('Editor failed to load');
		}

		return () => {
			try {
				if (editorRef.current && typeof editorRef.current.destroy === 'function') {
					editorRef.current.destroy();
				}
			} catch (e) {
				// noop
			}
			editorRef.current = null;
			setEditorReady(false);
		};
		// We deliberately do NOT depend on mjmlString or parentDocId here —
		// the editor instance must persist across template switches. When
		// parentDocId changes, the load effect calls editor.setComponents(...)
		// to swap the document in-place; destroying + recreating the editor
		// loses the queued setMjmlString and reads a stale closure mjmlString,
		// re-initializing with the prior template's content (the click-doesn't-
		// switch-preview bug).
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [templateLoading]);

	// Tick "Saved Xs ago" label
	useEffect(() => {
		const id = setInterval(() => setTickKey(k => k + 1), 10000);
		return () => clearInterval(id);
	}, []);

	useEffect(() => {
		if (editingSubject && subjectInputRef.current) {
			subjectInputRef.current.focus();
			subjectInputRef.current.select();
		}
	}, [editingSubject]);

	// Persist viewMode whenever the user toggles. We only write when the user
	// actually flips — initial reads happen via readStoredView() at mount.
	const flipViewMode = (next) => {
		setViewMode(next);
		writeStoredView(next);
	};

	// Load verified sender identities once after sign-in. The inline Send-test
	// flow needs a verified sender so we eager-load on mount rather than waiting
	// for a panel open (the panel is gone now).
	useEffect(() => {
		if (sendersLoaded || sendersLoading) return;
		let cancelled = false;
		const verifiedemail = sessionStorage.getItem("verifiedemail") || '';
		if (!verifiedemail) return;

		async function loadSenders() {
			setSendersLoading(true);
			try {
				axios.defaults.baseURL = getAxiosBaseUrl();
				const res = await axios.post('/list_identities', {user_email: verifiedemail});
				const list = (res && res.data && Array.isArray(res.data.identities)) ? res.data.identities : [];
				const checks = await Promise.all(list.map((email) =>
					axios.post('/check_identity', {email})
						.then((r) => {
							const d = r && r.data ? r.data : {};
							const v = (d.verification_status || '').toString();
							const ok = d.verified === true || /success/i.test(v);
							return ok ? email : null;
						})
						.catch(() => null)
				));
				if (cancelled) return;
				const verified = checks.filter(Boolean);
				setVerifiedSenders(verified);
				setSendFromAddr((prev) => {
					if (prev && verified.includes(prev)) return prev;
					return verified[0] || '';
				});
			} catch (err) {
				if (!cancelled) {
					console.log('list_identities error', err);
					setVerifiedSenders([]);
				}
			} finally {
				if (!cancelled) {
					setSendersLoading(false);
					setSendersLoaded(true);
				}
			}
		}
		loadSenders();
		return () => { cancelled = true; };
	}, [sendersLoaded, sendersLoading]);

	// Load all of the user's saved templates once so the preview-mode browse
	// strip can render thumbnails. We render MJML→HTML in the browser
	// (renderMjml is memoized in src/lib/mjmlPreview.js) so this is cheap.
	useEffect(() => {
		const verifiedemail = sessionStorage.getItem("verifiedemail") || '';
		if (!verifiedemail) return;
		let cancelled = false;
		(async () => {
			try {
				axios.defaults.baseURL = getBaseUrlNodeServer();
				const resp = await axios.post('getusersavedtemplates', {verifiedemail});
				if (cancelled) return;
				const rows = Array.isArray(resp.data) ? resp.data : [];
				setMyTemplates(rows.map((t) => {
					const userCats = Array.isArray(t.selectedCategories) ? t.selectedCategories.filter(Boolean) : [];
					return {
						_id: t._id,
						subject: t.subject || 'Untitled',
						content: t.content || '',  // raw MJML — used for hot-swap when switching templates in preview
						html: renderMjml(t.content || ''),
						categories: userCats.length > 0 ? userCats : [inferCategory(t.subject, t.content)],
					};
				}));
			} catch (err) {
				if (!cancelled) console.log('Could not load templates list for browse strip', err && err.message);
			}
		})();
		return () => { cancelled = true; };
	}, []);

	// "Back to my templates" — reuse browser history when possible so any
	// filter/search query params on /savedtemplates round-trip; fall back to
	// a fresh navigation when the user opened the editor directly.
	function goBackToTemplates() {
		try {
			if (typeof window !== 'undefined' && window.history.length > 1 && document.referrer && /\/savedtemplates/.test(document.referrer)) {
				navigate(-1);
				return;
			}
		} catch (e) { /* ignore */ }
		navigate('/savedtemplates');
	}

	const stripCategories = useMemo(() => {
		const set = new Set();
		myTemplates.forEach(t => (t.categories || []).forEach(c => c && set.add(c)));
		return Array.from(set).sort();
	}, [myTemplates]);

	const stripFiltered = useMemo(() => {
		if (stripCategory === 'all') return myTemplates;
		return myTemplates.filter(t => (t.categories || []).includes(stripCategory));
	}, [myTemplates, stripCategory]);

	// Index of the current template within the filtered strip — drives the
	// "Prev / Next" arrows in preview. -1 if the current template is filtered
	// out by the chip selection (then prev/next jumps to the first match).
	const stripIndex = useMemo(
		() => stripFiltered.findIndex(t => t._id === parentDocId),
		[stripFiltered, parentDocId]
	);
	const stripPrevId = stripFiltered.length > 0
		? stripFiltered[(stripIndex <= 0 ? stripFiltered.length : stripIndex) - 1]._id
		: null;
	const stripNextId = stripFiltered.length > 0
		? stripFiltered[(stripIndex < 0 ? 0 : (stripIndex + 1) % stripFiltered.length)]._id
		: null;
	function goToTemplate(id) {
		if (!id || id === parentDocId) return;
		navigate('/editor/' + id);
	}

	function copyTemplateId() {
		if (!parentDocId) return;
		try {
			if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
				navigator.clipboard.writeText(parentDocId);
				toast.success('Template ID copied', {autoClose: 1500});
			} else {
				// Fallback for non-secure contexts
				const ta = document.createElement('textarea');
				ta.value = parentDocId;
				document.body.appendChild(ta);
				ta.select();
				document.execCommand('copy');
				document.body.removeChild(ta);
				toast.success('Template ID copied', {autoClose: 1500});
			}
		} catch (e) {
			toast.error("Couldn't copy template ID");
		}
	}

	const categoriesDirty = JSON.stringify(selectedCategories) !== JSON.stringify(savedCategories);
	const subjectDirty = subject !== savedSubject;
	const mjmlDirty = !!mjmlString && mjmlString !== savedMjml;
	// For a brand-new blank doc (no id yet), there's no saved baseline at all,
	// so any non-empty MJML counts as dirty (otherwise the user can never save
	// for the first time). For existing templates, only real diffs are dirty.
	const hasChanges = parentDocId == null
		? !!mjmlString
		: !!(mjmlDirty || subjectDirty || categoriesDirty);

	// Memoized HTML for the preview iframe. renderMjml() itself memoizes by the
	// MJML string, but useMemo here keeps React from creating a new srcDoc
	// reference on every parent render, which would force a flicker reload of
	// the iframe.
	const previewHtml = useMemo(() => {
		if (viewMode !== 'preview') return '';
		return renderMjml(mjmlString || EMPTY_MJML);
	}, [viewMode, mjmlString]);

	// Delete a template. Two callers:
	//   - top-bar trash icon → deletes the currently-viewed template, then
	//     navigates to a sibling in the rail (or back to /savedtemplates if
	//     none remain).
	//   - per-card trash icon in the rail → deletes that template and removes
	//     it from the in-memory list without leaving the editor (unless the
	//     deleted one was the current).
	const deleteTemplateById = async (id, { fromCard = false } = {}) => {
		if (!id) return;
		const verifiedemail = sessionStorage.getItem('verifiedemail');
		const target = myTemplates.find(t => t._id === id);
		const label = target ? (target.subject || 'this template') : 'this template';
		if (!window.confirm(`Delete "${label}"? This can't be undone.`)) return;
		try {
			axios.defaults.baseURL = getBaseUrlNodeServer();
			await axios.post('deleteusertemplate', { verifiedemail, templateId: id });
			const remaining = myTemplates.filter(t => t._id !== id);
			setMyTemplates(remaining);
			toast.success('Template deleted');
			if (!fromCard || id === parentDocId) {
				// We just removed the active template — jump to the next
				// available one in the filtered strip (or fall back to the
				// templates index if the user's library is now empty).
				const filtered = stripCategory === 'all'
					? remaining
					: remaining.filter(t => (t.categories || []).includes(stripCategory));
				const fallback = filtered[0] || remaining[0];
				if (fallback) {
					navigate('/editor/' + fallback._id);
				} else {
					navigate('/savedtemplates');
				}
			}
		} catch (err) {
			console.error('delete failed', err);
			toast.error('Failed to delete template');
		}
	};

	const saveUserTemplate = async () => {
		var verifiedemail = sessionStorage.getItem("verifiedemail");
		const mjmlStr = mjmlString;
		const subject1 = subject;
		setSaving(true);
		if (parentDocId == null) {
			try {
				const response = await fetch(getBaseUrlNodeServer() + 'createtemplate', {
					method: 'post',
					headers: {'Content-Type': 'application/json'},
					body: JSON.stringify({
						'mjmlStr': mjmlStr,
						'subject': subject1,
						'verifiedemail': verifiedemail
					})
				});
				const result = await response.json();
				setparentDocId(result.insertedId);
				setSavedMjml(mjmlStr);
				setSavedSubject(subject1);
				setSavedCategories(selectedCategories);
				setLastSavedAt(Date.now());
				toast.success('Template created');
				navigate('/editor/' + result.insertedId);
			} catch (error) {
				console.error('Error during fetch:', error.message);
				toast.error('Could not save template');
			} finally {
				setSaving(false);
			}
		} else {
			try {
				await fetch(getBaseUrlNodeServer() + 'saveusertemplate', {
					method: 'post',
					headers: {'Content-Type': 'application/json'},
					body: JSON.stringify({
						'mjmlStr': mjmlStr,
						'subject': subject1,
						'templateId': {parentDocId: parentDocId},
						'verifiedemail': verifiedemail,
						'selectedCategories': selectedCategories
					})
				});
				setSavedMjml(mjmlStr);
				setSavedSubject(subject1);
				setSavedCategories(selectedCategories);
				setLastSavedAt(Date.now());
				toast.success('Saved');
			} catch (error) {
				console.error('Error during fetch:', error.message);
				toast.error('Could not save template');
			} finally {
				setSaving(false);
			}
		}
	};

	const sendTestEmail = async () => {
		const addr = (testEmail || '').trim();
		if (!addr) {
			setTestEmailError('Enter an email first');
			if (testEmailInputRef.current) testEmailInputRef.current.focus();
			return;
		}
		setTestEmailError('');
		if (!sendFromAddr) {
			toast.error('No verified sender available — add one in /identities');
			return;
		}
		setSendingTest(true);
		try {
			await saveUserTemplate(); // Always save before sending
			axios.defaults.baseURL = getAxiosBaseUrl();
			await axios.post('/emailcampaign', {
				templateIdSelected: parentDocId,
				sendToList: addr,
				sendFrom: sendFromAddr
			});
			toast.success(`Test email sent to ${addr}`);
			// Push onto the history list for next time.
			setTestEmailHistory(pushTestEmail(addr));
		} catch (err) {
			console.log(err);
			toast.error('Could not send test email');
		} finally {
			setSendingTest(false);
		}
	};

	const openAskClaude = () => {
		// If no key yet, send the user to the settings page rather than open a
		// modal that can only fail.
		const key = getStoredKey();
		if (!key) {
			toast.info('Add your Anthropic API key first');
			navigate('/settings/anthropic-key');
			return;
		}
		setAskPrompt('');
		setAskModel(getLastUsedModel());
		setShowAskClaude(true);
	};

	const closeAskClaude = () => {
		if (generating) return; // don't let users dismiss mid-flight
		setShowAskClaude(false);
		setAskPrompt('');
	};

	const runAskClaude = async () => {
		const userPrompt = (askPrompt || '').trim();
		if (!userPrompt) {
			toast.error('Describe what you want first');
			return;
		}
		setGenerating(true);
		try {
			const { mjml } = await generateMjmlTemplate({ prompt: userPrompt, model: askModel });
			setLastUsedModel(askModel);

			// Push the generated MJML straight into the live grapesjs editor.
			// setComponents() works at any time and replaces the document — same
			// path the import-modal plugin uses internally.
			const editor = editorRef.current;
			if (editor && typeof editor.setComponents === 'function') {
				editor.setComponents(mjml);
			} else {
				// Fall back to the React state if the editor instance isn't ready
				// (e.g. during the initial mount race) — the next mount will pick it up.
				setMjmlString(mjml);
			}

			// Reflect the change in the dirty/save bar.
			setMjmlString(mjml);
			toast.success('Template generated — review and save when ready');
			setShowAskClaude(false);
			setAskPrompt('');
		} catch (err) {
			console.log('ask claude error', err);
			if (err instanceof AnthropicError && err.code === 'missing_key') {
				toast.error(<span>
					{err.message}{' '}
					<button
						type="button"
						onClick={() => { setShowAskClaude(false); navigate('/settings/anthropic-key'); }}
						className="underline font-semibold">
						Set up your key
					</button>
				</span>);
			} else {
				const msg = (err && err.message) ? err.message : 'Could not generate template';
				toast.error(msg);
			}
			// Keep the modal open so the user can tweak the prompt + retry.
		} finally {
			setGenerating(false);
		}
	};

	const commitSubject = () => {
		const next = (subjectDraft || '').trim();
		if (next) setSubject(next);
		setEditingSubject(false);
	};

	const beginEditSubject = () => {
		setSubjectDraft(subject || '');
		setEditingSubject(true);
	};

	if (!signedin) {
		return (
			<div className="min-h-screen bg-white flex items-center justify-center px-6 py-24">
				<div className="w-full max-w-md">
					<SigninForm/>
				</div>
			</div>
		);
	}

	const saveLabel = formatRelative(lastSavedAt, tickKey);
	const isPreview = viewMode === 'preview';
	// Hide the full-page spinner whenever the user is in preview — the
	// preview overlay covers the editor, so any grapesjs lifecycle (initial
	// mount, parentDocId remount) is invisible to them. The spinner was the
	// thing making template switches feel like a heavy refresh.
	const showSpinner = !isPreview && (templateLoading || !editorReady);
	const saveDisabled = saving || !hasChanges;

	return (
		<div className="h-screen bg-slate-50 flex flex-col overflow-hidden">
			{/* Emerald theme overrides for grapesjs.
			    Critical: explicitly force .gjs-editor-cont and .gjs-editor
			    to fill height. grapesjs ships .gjs-editor with height:100%
			    but that needs an ancestor with a *definite* height — using
			    h-screen on the outer wrapper makes the chain definite
			    instead of relying on min-h-screen (which leaves
			    `height: auto` and collapses descendant percentages to 0). */}
			<style>{`
				.gjs-editor-cont, .gjs-editor { height: 100% !important; min-height: 0 !important; }
				.gjs-cv-canvas { height: 100% !important; }
				${grapesjsThemeCss}
			`}</style>

			{/* Sticky top bar */}
			<header
				className="bg-white border-b border-slate-100 sticky top-0 z-40 backdrop-blur supports-[backdrop-filter]:bg-white/80">
				<div className="px-4 md:px-6 py-3 flex items-center gap-3">
					<button
						type="button"
						onClick={goBackToTemplates}
						title="Back to my templates"
						className="inline-flex items-center gap-1.5 rounded-full pl-2 pr-3 md:pr-4 py-1.5 text-sm font-medium text-slate-700 hover:text-slate-900 border border-slate-200 hover:bg-slate-50 transition shrink-0">
						<CaretLeft size={16} weight="bold"/>
						<span className="hidden sm:inline">My templates</span>
					</button>

					<div className="flex items-center gap-2 min-w-0 flex-1">
						<span className="text-slate-300 shrink-0 hidden sm:inline">/</span>
						{parentDocId && (
							<button
								type="button"
								onClick={copyTemplateId}
								title={`Copy template ID: ${parentDocId}`}
								aria-label="Copy template ID"
								className="hidden md:inline-flex items-center justify-center w-7 h-7 rounded-md text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 transition shrink-0">
								<Copy size={14} weight="bold"/>
							</button>
						)}
						{editingSubject ? (
							<input
								ref={subjectInputRef}
								type="text"
								value={subjectDraft}
								onChange={(e) => setSubjectDraft(e.target.value)}
								onBlur={commitSubject}
								onKeyDown={(e) => {
									if (e.key === 'Enter') commitSubject();
									if (e.key === 'Escape') setEditingSubject(false);
								}}
								className="border border-emerald-300 rounded-lg px-3 py-1.5 text-sm font-semibold text-slate-900 focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 outline-none min-w-0 flex-1 max-w-md"/>
						) : (
							<button
								type="button"
								onClick={beginEditSubject}
								title="Click to rename"
								className="group inline-flex items-center gap-2 min-w-0 rounded-lg px-2 py-1 -mx-1 hover:bg-slate-50 transition">
								<span className="text-sm font-semibold text-slate-900 truncate max-w-[40vw]">
									{subject || PLACEHOLDER_SUBJECT}
								</span>
								<PencilSimple size={14} className="text-slate-400 opacity-0 group-hover:opacity-100 transition shrink-0"/>
							</button>
						)}
					</div>

					<div className="hidden md:flex items-center mr-2">
						{saving ? (
							<span className="text-xs font-medium text-emerald-600">Saving…</span>
						) : hasChanges ? (
							<span className="text-xs font-medium text-amber-600">Unsaved changes</span>
						) : saveLabel ? (
							<span className="text-xs text-slate-500">{saveLabel}</span>
						) : (
							<span className="text-xs text-slate-400">No changes yet</span>
						)}
					</div>

					{isAdmin && (
						<button
							type="button"
							onClick={() => setShowCategoryBar(s => !s)}
							title="Tag categories (admin)"
							className={
								"hidden md:inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium border transition " +
								(showCategoryBar
									? "bg-emerald-50 text-emerald-700 border-emerald-200"
									: "bg-white text-slate-600 border-slate-200 hover:bg-slate-50")
							}>
							<Tag size={14} weight="bold"/>
							Categories{selectedCategories.length ? ` · ${selectedCategories.length}` : ''}
						</button>
					)}

					{/* Edit / Preview toggle. Replaces the tiny grapesjs eye icon — the
					    button reads as the action ("Edit" while in preview, "Preview"
					    while in edit) and uses the same chrome as the rest of the app. */}
					<button
						type="button"
						onClick={() => flipViewMode(isPreview ? 'edit' : 'preview')}
						title={isPreview ? 'Switch to edit mode' : 'Switch to preview mode'}
						className={
							"rounded-full px-4 md:px-5 py-2 text-sm font-medium inline-flex items-center gap-1.5 transition border " +
							(isPreview
								? "bg-emerald-500 hover:bg-emerald-600 text-white border-transparent shadow-sm"
								: "bg-white border-slate-200 hover:bg-slate-50 text-slate-900")
						}>
						{isPreview
							? (<><PencilSimple size={15} weight="bold"/><span className="hidden sm:inline">Edit</span></>)
							: (<><Eye size={15} weight="bold"/><span className="hidden sm:inline">Preview</span></>)
						}
					</button>

					<button
						type="button"
						onClick={openAskClaude}
						title="Generate a template with Claude"
						className="bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 text-emerald-700 rounded-full px-4 md:px-5 py-2 text-sm font-semibold inline-flex items-center gap-1.5 transition">
						<Sparkle size={15} weight="bold"/>
						<span className="hidden sm:inline">Ask Claude</span>
					</button>

					{parentDocId && (
						<button
							type="button"
							onClick={() => deleteTemplateById(parentDocId)}
							title="Delete this template"
							aria-label="Delete this template"
							className="bg-white border border-rose-200 hover:bg-rose-50 text-rose-600 rounded-full w-9 h-9 inline-flex items-center justify-center transition">
							<Trash size={16} weight="bold"/>
						</button>
					)}

					<button
						type="button"
						onClick={saveUserTemplate}
						disabled={saveDisabled}
						title={saveDisabled && !saving ? 'No changes to save' : 'Save'}
						className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full px-4 md:px-5 py-2 text-sm font-semibold shadow-sm inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed transition">
						<FloppyDisk size={15} weight="bold"/>
						{saving ? 'Saving…' : 'Save'}
					</button>
				</div>

				{isAdmin && showCategoryBar && (
					<div className="px-4 md:px-6 pb-3 -mt-1 border-t border-slate-100 pt-3 bg-slate-50/40">
						<div className="flex items-center gap-3">
							<span className="text-xs font-medium uppercase tracking-wide text-slate-500 shrink-0">Categories</span>
							<div className="overflow-x-auto">
								<CategorySelectionButtons
									selectedCategories={selectedCategories}
									setSelectedCategories={setSelectedCategories}
									singleSelectionOnly={false}/>
							</div>
						</div>
					</div>
				)}

				{/* Send-test row — tinted emerald so it reads as a distinct action
				    surface (not just another grey toolbar). Email input on the
				    left, primary Send button on the right, helper text below
				    spells out exactly where the mail goes. */}
				<div className="px-4 md:px-6 pb-3 -mt-1 border-t border-slate-100 pt-3 bg-emerald-50/60">
					<div className="flex flex-col sm:flex-row sm:items-center gap-2">
						<label htmlFor="inline-test-email" className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-emerald-700 shrink-0 sm:w-28">
							<PaperPlaneTilt size={14} weight="bold"/>
							Send test
						</label>
						<div className="flex-1 flex items-stretch gap-2 min-w-0">
							<input
								id="inline-test-email"
								ref={testEmailInputRef}
								type="email"
								list="inline-test-email-history"
								value={testEmail}
								onChange={(e) => { setTestEmail(e.target.value); if (testEmailError) setTestEmailError(''); }}
								onKeyDown={(e) => { if (e.key === 'Enter') sendTestEmail(); }}
								placeholder="test@example.com"
								className={
									"flex-1 min-w-0 bg-white border rounded-full px-4 py-2 text-sm focus:ring-2 focus:ring-emerald-100 outline-none transition " +
									(testEmailError
										? "border-amber-400 focus:border-amber-500"
										: "border-emerald-200 focus:border-emerald-400")
								}/>
							<datalist id="inline-test-email-history">
								{testEmailHistory.map((e) => (
									<option key={e} value={e}/>
								))}
							</datalist>
							<button
								type="button"
								onClick={sendTestEmail}
								disabled={sendingTest}
								className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full px-4 md:px-5 py-2 text-sm font-semibold shadow-sm inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed transition shrink-0">
								<PaperPlaneTilt size={15} weight="bold"/>
								{sendingTest ? 'Sending…' : 'Send test'}
							</button>
						</div>
					</div>
					<div className="mt-1.5 sm:pl-[7.5rem] text-xs text-emerald-900/70">
						{testEmailError ? (
							<span className="text-amber-700 font-medium">{testEmailError}</span>
						) : (
							<>
								Will send a real email from <span className="font-medium text-emerald-900">{SEND_FROM_DISPLAY}</span>
								{testEmail.trim() ? <> to <span className="font-medium text-emerald-900">{testEmail.trim()}</span></> : null}
								{(!sendersLoading && verifiedSenders.length === 0) && (
									<> — <Link to="/identities" className="underline font-medium hover:text-amber-900">add a verified sender</Link> first</>
								)}
								.
							</>
						)}
					</div>
				</div>
			</header>

			{/* Editor body — full bleed. flex-1 + min-h-0 makes this
			    flex item shrinkable so it actually fills remaining height
			    (without min-h-0, flex children prefer content-sized which
			    leaves grapesjs with 0 inner height even on h-screen). */}
			<main className="flex-1 min-h-0 w-full relative">
				{/* GrapesJS container — always mounted; we hide it (visually only)
				    while in preview mode by overlaying the preview pane on top.
				    We must NOT unmount or display:none this — grapesjs reads
				    container dimensions on mount and depends on a non-zero size. */}
				<div
					ref={editorContainerRef}
					className="w-full h-full"
				/>

				{/* Preview overlay. Position absolute over the editor so grapesjs
				    keeps its DOM/sizes intact underneath. */}
				{isPreview && (
					<div className="absolute inset-0 z-30 bg-slate-100 flex flex-col">
						{/* Preview header: "My templates" pill on the far left, then
						    device toggle + prev/next nav + counter pushed to the right. */}
						<div className="bg-white border-b border-slate-200 px-4 py-2 flex items-center justify-between gap-3">
							<button
								type="button"
								onClick={goBackToTemplates}
								title="Back to my templates"
								className="inline-flex items-center gap-1.5 rounded-full pl-2 pr-3 md:pr-4 py-1.5 text-xs font-medium text-slate-700 hover:text-slate-900 border border-slate-200 hover:bg-slate-50 transition shrink-0">
								<CaretLeft size={14} weight="bold"/>
								<span className="hidden sm:inline">My templates</span>
							</button>
							<div className="flex items-center gap-3">
							<div className="inline-flex rounded-full border border-slate-200 bg-slate-50 p-1">
								{[
									{key: 'desktop', label: 'Desktop', Icon: Desktop},
									{key: 'tablet', label: 'Tablet', Icon: DeviceTablet},
									{key: 'mobile', label: 'Mobile', Icon: DeviceMobile},
								].map(({key, label, Icon}) => {
									const active = previewDevice === key;
									return (
										<button
											key={key}
											type="button"
											onClick={() => setPreviewDevice(key)}
											title={label}
											className={
												"inline-flex items-center gap-1.5 px-3 md:px-4 py-1.5 rounded-full text-xs font-medium transition " +
												(active
													? "bg-white text-emerald-700 shadow-sm border border-emerald-200"
													: "text-slate-600 hover:text-slate-900")
											}>
											<Icon size={15} weight={active ? "bold" : "regular"}/>
											<span className="hidden sm:inline">{label}</span>
										</button>
									);
								})}
							</div>
							<div className="flex items-center gap-1.5">
								<button
									type="button"
									onClick={() => goToTemplate(stripPrevId)}
									disabled={stripFiltered.length < 2}
									title="Previous template"
									aria-label="Previous template"
									className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-slate-200 bg-white text-slate-600 hover:text-slate-900 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition">
									<CaretLeft size={16} weight="bold"/>
								</button>
								{stripFiltered.length > 0 && stripIndex >= 0 && (
									<span className="hidden md:inline text-xs text-slate-500 px-1.5 tabular-nums">
										{stripIndex + 1} / {stripFiltered.length}
									</span>
								)}
								<button
									type="button"
									onClick={() => goToTemplate(stripNextId)}
									disabled={stripFiltered.length < 2}
									title="Next template"
									aria-label="Next template"
									className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-slate-200 bg-white text-slate-600 hover:text-slate-900 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition">
									<CaretRight size={16} weight="bold"/>
								</button>
							</div>
							</div>
						</div>
						{/* Body row: full-width preview on the left, vertical
						    template rail on the right. The center pane gets the
						    full remaining width (minus the rail) so the chosen
						    device frame can breathe without the bottom strip
						    eating vertical real estate. */}
						<div className="flex-1 min-h-0 flex">
							<div className="flex-1 min-h-0 overflow-auto py-6 px-4 flex justify-center">
								<div
									className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden"
									style={{
										width: `min(100%, ${DEVICE_WIDTHS[previewDevice]}px)`,
										transition: 'width 200ms ease',
									}}>
									<iframe
										key={parentDocId || 'new'}
										title="Email preview"
										srcDoc={previewHtml}
										className="block w-full h-full"
										style={{
											width: '100%',
											minHeight: '70vh',
											border: 'none',
										}}/>
								</div>
							</div>

							{/* Right rail — category chips pinned at the top, vertical
							    scroll list of template thumbnails below. Replaces the
							    old bottom strip; keeps the entire center area free for
							    the preview frame. Hidden on narrow screens to avoid
							    crushing the preview pane on phones. */}
							{myTemplates.length > 1 && (
								<aside className="hidden lg:flex w-[520px] shrink-0 bg-white border-l border-slate-200 flex-col min-h-0">
									{stripCategories.length > 0 && (
										<div className="px-3 pt-3 pb-2 flex items-center gap-2 overflow-x-auto border-b border-slate-100 shrink-0">
											<button
												type="button"
												onClick={() => setStripCategory('all')}
												className={
													"shrink-0 inline-flex items-center rounded-full px-3 py-1 text-xs font-medium transition border " +
													(stripCategory === 'all'
														? "bg-emerald-500 text-white border-transparent"
														: "bg-white text-slate-600 border-slate-200 hover:bg-slate-50")
												}>
												All <span className="ml-1.5 opacity-75">{myTemplates.length}</span>
											</button>
											{stripCategories.map((cat) => {
												const count = myTemplates.filter(t => (t.categories || []).includes(cat)).length;
												const active = stripCategory === cat;
												return (
													<button
														key={cat}
														type="button"
														onClick={() => setStripCategory(cat)}
														className={
															"shrink-0 inline-flex items-center rounded-full px-3 py-1 text-xs font-medium transition border " +
															(active
																? "bg-emerald-500 text-white border-transparent"
																: "bg-white text-slate-600 border-slate-200 hover:bg-slate-50")
														}>
														{cat} <span className="ml-1.5 opacity-75">{count}</span>
													</button>
												);
											})}
										</div>
									)}
									<div className="flex-1 min-h-0 overflow-y-auto px-3 py-3 grid grid-cols-2 gap-3">
										{stripFiltered.length === 0 ? (
											<div className="col-span-2 text-xs text-slate-400 py-6 px-2">No other templates in this category.</div>
										) : stripFiltered.map((t) => {
											const isCurrent = t._id === parentDocId;
											return (
												<div
													key={t._id}
													aria-current={isCurrent ? 'true' : undefined}
													className={
														"relative flex flex-col h-[500px] rounded-lg overflow-hidden border transition bg-white group " +
														(isCurrent
															? "border-emerald-500 ring-2 ring-emerald-200"
															: "border-slate-200 hover:border-emerald-300 hover:shadow-sm")
													}>
													<button
														type="button"
														onClick={(e) => { e.stopPropagation(); deleteTemplateById(t._id, { fromCard: true }); }}
														title="Delete template"
														aria-label="Delete template"
														className="absolute top-2 right-2 z-10 w-8 h-8 inline-flex items-center justify-center rounded-full bg-white/90 backdrop-blur border border-rose-200 text-rose-600 hover:bg-rose-50 shadow-sm opacity-0 group-hover:opacity-100 focus:opacity-100 transition">
														<Trash size={14} weight="bold"/>
													</button>
													<button
														type="button"
														onClick={() => { if (!isCurrent) navigate('/editor/' + t._id); }}
														title={t.subject}
														className={
															"flex flex-col flex-1 min-h-0 text-left " +
															(isCurrent ? "cursor-default" : "cursor-pointer")
														}>
														<div className="relative flex-1 w-full bg-slate-50 overflow-hidden">
															<iframe
																title={`thumb-${t._id}`}
																srcDoc={t.html || '<html><body style="margin:0;background:#f8fafc;"></body></html>'}
																sandbox=""
																className="absolute top-0 left-0 pointer-events-none border-0"
																style={{
																	transform: 'scale(0.4)',
																	transformOrigin: 'top left',
																	width: '250%',
																	height: '250%',
																}}/>
														</div>
														<div className="px-2.5 py-2 text-xs font-medium text-slate-700 truncate border-t border-slate-100 shrink-0">
															{t.subject}
														</div>
													</button>
												</div>
											);
										})}
									</div>
								</aside>
							)}
						</div>
					</div>
				)}

				{showSpinner && (
					<div className="absolute inset-0 z-40 flex items-center justify-center bg-slate-50/80 backdrop-blur-sm">
						<div className="flex items-center gap-3 text-slate-500">
							<svg className="animate-spin h-5 w-5 text-emerald-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
								<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
								<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
							</svg>
							<span className="text-sm font-medium">{templateLoading ? 'Loading template…' : 'Preparing editor…'}</span>
						</div>
					</div>
				)}
			</main>

			{/* Ask Claude modal */}
			{showAskClaude && (
				<div
					role="dialog"
					aria-modal="true"
					aria-labelledby="ask-claude-title"
					className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm px-4">
					<div className="w-full max-w-2xl rounded-2xl shadow-2xl bg-white">
						<div className="flex items-start justify-between px-6 pt-5 pb-3">
							<div className="flex items-center gap-3 min-w-0">
								<div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
									<Sparkle size={20} weight="duotone"/>
								</div>
								<div className="min-w-0">
									<h2 id="ask-claude-title" className="text-lg font-semibold text-slate-900">Generate a template</h2>
									<p className="text-xs text-slate-500 mt-0.5">Claude drafts MJML in seconds — review and tweak in the editor.</p>
								</div>
							</div>
							<button
								type="button"
								onClick={closeAskClaude}
								disabled={generating}
								aria-label="Close"
								className="p-2 -mr-2 -mt-1 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed transition">
								<X size={18}/>
							</button>
						</div>

						<div className="px-6 pb-6">
							<label htmlFor="ask-claude-prompt" className="block text-sm font-medium text-slate-700 mb-1.5">
								What email do you want?
							</label>
							<textarea
								id="ask-claude-prompt"
								value={askPrompt}
								onChange={(e) => setAskPrompt(e.target.value)}
								disabled={generating}
								rows={5}
								placeholder="Describe the email you want — tone, sections, audience, CTA, anything"
								className="w-full border border-slate-200 rounded-xl px-4 py-2.5 focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 outline-none transition disabled:bg-slate-50 disabled:text-slate-500"/>

							<div className="mt-3 flex flex-wrap gap-2">
								{EXAMPLE_PROMPTS.map((ex) => (
									<button
										key={ex.label}
										type="button"
										onClick={() => setAskPrompt(ex.prompt)}
										disabled={generating}
										className="text-xs px-3 py-1.5 rounded-full border border-slate-200 bg-white text-slate-600 hover:border-emerald-300 hover:text-emerald-700 hover:bg-emerald-50 disabled:opacity-50 disabled:cursor-not-allowed transition">
										{ex.label}
									</button>
								))}
							</div>

							<div className="mt-5 flex items-center gap-2">
								<label htmlFor="ask-claude-model" className="text-xs font-medium text-slate-600 shrink-0">
									Model
								</label>
								<select
									id="ask-claude-model"
									value={askModel}
									onChange={(e) => setAskModel(e.target.value)}
									disabled={generating}
									className="text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 bg-white focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 outline-none disabled:opacity-50">
									{AVAILABLE_MODELS.map((m) => (
										<option key={m.id} value={m.id}>{m.label}</option>
									))}
								</select>
							</div>

							{generating && (
								<div className="mt-5 flex items-center gap-3 text-sm text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-xl px-4 py-3">
									<svg className="animate-spin h-4 w-4 text-emerald-600 shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
										<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
										<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
									</svg>
									Claude is drafting your email…
								</div>
							)}

							<div className="mt-6 flex items-center justify-end gap-2">
								<button
									type="button"
									onClick={closeAskClaude}
									disabled={generating}
									className="bg-white border border-slate-200 hover:bg-slate-50 text-slate-900 rounded-full px-6 py-3 font-medium disabled:opacity-50 disabled:cursor-not-allowed transition">
									Cancel
								</button>
								<button
									type="button"
									onClick={runAskClaude}
									disabled={generating || !askPrompt.trim()}
									className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full px-6 py-3 font-semibold shadow-sm inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed transition">
									<Sparkle size={15} weight="bold"/>
									{generating ? 'Generating…' : 'Generate'}
								</button>
							</div>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}
